import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@ubisend/framer-motion';

import { useDelivery, useBot } from '../../../hooks';
import { scroll, groupMessages } from '../../../utilities';
import {
  DefaultMessageWrapper,
  DefaultGroupWrapper
} from '../../../Providers/BotProvider';
import Message from './Message';
import MessageGroup from './MessageGroup';
import MessageContainer from './MessageContainer';
import JumpToBottom from './JumpToBottom';
import Typing from './Typing';
import TypingIndicator from './TypingIndicator';

const Messages = ({ showJump }) => {
  const bottomRef = useRef(null);

  const {
    messages,
    MessageWrapper: PassedMessageWrapper,
    GroupWrapper: PassedGroupWrapper
  } = useBot();

  const { sendMessage, sendLocationMessage } = useDelivery();

  const MessageWrapper = PassedMessageWrapper || DefaultMessageWrapper;
  const GroupWrapper = PassedGroupWrapper || DefaultGroupWrapper;

  const groups = useMemo(() => {
    return messages.length > 0 && messages.reduce(groupMessages, []);
  }, [messages]);

  const scrollToBottom = useCallback(() => scroll(bottomRef.current), []);

  const snapToBottom = useCallback(() => scroll(bottomRef.current), []);

  /**
   * Scroll to bottom on every message send and receive.
   */
  useEffect(() => {
    if (messages && bottomRef.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  /**
   * Snap to bottom on render.
   */
  useLayoutEffect(() => {
    if (bottomRef.current) {
      snapToBottom();
    }
  }, [snapToBottom]);

  const hasHadClientResponse = groupIndex => {
    // checks to see if the message has has a response from the client since being sent
    const mutableGroups = [...groups];
    const nextMessages = mutableGroups.splice(groupIndex);
    if (nextMessages.some(e => e[0].direction === 'toServer')) {
      return true;
    }
  };

  return (
    <>
      <AnimatePresence>
        {showJump && <JumpToBottom onClick={scrollToBottom} />}
      </AnimatePresence>
      {!groups && (
        <MessageContainer>
          <TypingIndicator />
        </MessageContainer>
      )}
      {groups &&
        groups.map((messages, groupIndex) => (
          <GroupWrapper key={groupIndex} messages={messages} index={groupIndex}>
            <MessageGroup
              group={messages}
              previousGroup={groups[groupIndex - 1]}>
              {messages.map((message, messageKey) => {
                const isFromUser = message.direction === 'toServer';
                const isLocation = message.type === 'location';
                const labelPrefix = isFromUser ? 'Message from user: ' : 'Message from chatbot: ';
                const labelContent = isLocation
                  ? `Location (${message.content.latitude}, ${message.content.longitude})`
                  : message.content.text;

                const label = `${labelPrefix}${labelContent}`;
                const widgetLabel = `widget-message-${groupIndex}-${messageKey}`;

                return (
                  <MessageWrapper
                    index={messageKey}
                    message={message}
                    label={widgetLabel}
                    aria-label={widgetLabel}
                    key={`${groupIndex}-${messageKey}`}
                  >
                    <Message
                      label={label}
                      handleButtonClick={sendMessage}
                      handleButtonLocationClick={sendLocationMessage}
                      groupIndex={groupIndex}
                      group={messages}
                      groups={groups}
                      message={message}
                      hasHadReply={hasHadClientResponse(groupIndex)}
                    />
                  </MessageWrapper>
                );
              })}

            </MessageGroup>
          </GroupWrapper>
        ))}
      <Typing scrollToBottom={scrollToBottom} />
      <div ref={bottomRef} />
    </>
  );
};

Messages.propTypes = {
  showJump: PropTypes.bool
};

export default Messages;
